import { memo, useEffect, useState } from "react";
import { type AddressData } from "../Address";
import { useTranslation } from "react-i18next";
import { CreateEditAddressModal } from "./components/CreateEditAddressModal";
import { deleteAddress } from "~/features/cart/cart/api/deleteAddress";
import { editAddress } from "~/features/cart/cart/api/editAddress";
import { createAddress } from "~/features/cart/cart/api/createAddress";
import { createAddressLine1 } from "~/utils/addressUtils";
import { Button, Radio, Stack, styled, Typography } from "~/common/mui";
import { Icon } from "~/common/components/icon/Icon";
import { C_OUTLINE } from "~/common/components/icon/Colors";
import Modal from "~/common/components/Modal";
import AddIcon from "@mui/icons-material/Add";
import { IconType } from "~/common/components/icon/IconType";

interface Props {
  addresses: AddressData[];
  selectedAddress: AddressData;
  addresssLabel: string;
  updateAddress: (selected: AddressData, temporary?: boolean) => void;
  customerNumber: number | string;
  countriesList: {
    isocode: string;
    name: string;
  }[];
  canAddAddress: boolean;
  onClose: () => void;
  isOpenAddress: boolean;
}

const AddressDropdown = (props: Props) => {
  const {
    addresses,
    addresssLabel,
    updateAddress,
    customerNumber,
    countriesList,
    canAddAddress,
    selectedAddress,
    onClose,
    isOpenAddress,
  } = props;
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number>(0); // zero because default shipping addres is always first
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [addressChosenToEdit, setAddressChosenToEdit] = useState<AddressData | null>(null);
  const [sortedAddresses, setSortedAddresses] = useState<AddressData[]>([]);

  useEffect(() => {
    sortAddressesAndSetState(addresses);
  }, [addresses]);

  const sortAddressesAndSetState = (addressesForSort: AddressData[], newSelectedId?: string) => {
    let defaultAddress: AddressData;
    const approvedAddress: AddressData[] = [];
    const editableAddress: AddressData[] = [];

    //manage putting to the right array
    addressesForSort.forEach((address, index) => {
      if (address.default) {
        defaultAddress = address;
      } else if (address.editable) {
        editableAddress.push(address);
      } else {
        approvedAddress.push(address);
      }
    });

    //manage sorting alphabeticly
    approvedAddress.sort((a, b) => (a.companyName + a.lines[0]).localeCompare(b.companyName + b.lines[0]));
    editableAddress.sort((a, b) => (a.companyName + a.lines[0]).localeCompare(b.companyName + b.lines[0]));

    //put in right order
    const sortedArray: AddressData[] = [defaultAddress!, ...approvedAddress, ...editableAddress];

    setSortedAddresses(sortedArray);
    if (newSelectedId) {
      sortedArray.forEach((address, index) => {
        if (address.id === newSelectedId) {
          setSelectedIndex(index);
          updateAddress(address, address.temporary);
        }
      });
    } else {
      sortedArray.forEach((address, index) => {
        if (address.id === selectedAddress.id) {
          setSelectedIndex(index);
        }
      });
    }
  };

  const selectAddress = (index: number) => {
    if (sortedAddresses[index]?.editable) {
      updateAddress(sortedAddresses[index], sortedAddresses[index].temporary);
    } else {
      updateAddress(sortedAddresses[index]);
    }
    // setSelectedIndex(index);
    onClose();
  };

  const deleteAdressOnFront = (addressId: string) => {
    setSortedAddresses((prev) => prev.filter((address) => address.id !== addressId));

    if (sortedAddresses[selectedIndex!].id === addressId) {
      selectAddress(0);
    }
  };

  const editAdressOnFront = (addressId: string, newAddress: AddressData) => {
    const newAddressList = [...sortedAddresses];
    const editingAddressIndex = newAddressList.findIndex((address) => address.id === addressId);
    newAddressList[editingAddressIndex] = newAddress;
    sortAddressesAndSetState(newAddressList);
    if (addressId === sortedAddresses[selectedIndex!].id) {
      updateAddress(newAddress, newAddress.temporary);
    }
  };

  const addAdressOnFront = (newAddress: AddressData) => {
    const newAddressList = [...sortedAddresses];
    newAddressList.push(newAddress);
    sortAddressesAndSetState(newAddressList, newAddress.id);
  };

  const openOrCloseModal = () => {
    setIsModalOpen((prevState) => !prevState);
  };

  const renderAddressesLabel = (isDefault: boolean, isEditable: boolean) => {
    return isDefault
      ? `${t("checkoutPage.addressDropdown.default")} ${customerNumber}`
      : isEditable
      ? t("checkoutPage.addressDropdown.editable")
      : t("checkoutPage.addressDropdown.approved");
  };

  const getAddressLine1 = (addressLines: string[]) => {
    if (addressLines.length > 2) {
      return <div>{addressLines[0] + " " + addressLines[1]}</div>;
    }
    return <div>{addressLines[0]}</div>;
  };

  const getAddressLine2 = (addressLines: string[]) => {
    if (addressLines.length > 2) {
      return <div>{addressLines[2]}</div>;
    }
    return addressLines[1] ? <div>{addressLines[1]}</div> : null;
  };

  const actveClass = isDropdownOpen ? "active" : "";
  // Leftover old code, untangle later
  const idOfSelectedAddress = sortedAddresses[selectedIndex!]?.id ?? "";
  const defaulAddress = sortedAddresses[0];
  const footer = () => (
    <FooterStack>
      {canAddAddress && (
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
            setIsModalOpen(true);
            setAddressChosenToEdit(null);
          }}
        >
          {t("checkoutPage.addressDropdown.addNew")}
        </Button>
      )}

      <Stack direction="row" gap={1} sx={{ ml: "auto" }}>
        <Button variant="text" onClick={onClose}>
          {t("generic.back")}
        </Button>
        <Button onClick={() => selectAddress(selectedIndex)}>{t("selectAddressPopup.title")}</Button>
      </Stack>
    </FooterStack>
  );

  const renderAddressCard = (address: AddressData, index: number) => (
    <Stack
      direction={"row"}
      sx={{
        padding: "8px 16px",
        justifyContent: "space-between",
        alignItems: "flex-start",
        border: `1px solid ${C_OUTLINE}`,
        borderRadius: "12px",
      }}
    >
      <Radio
        checked={selectedIndex == index}
        onChange={() => {
          setSelectedIndex(index);
        }}
        value="a"
        name="radio-buttons"
        inputProps={{ "aria-label": "A" }}
      />
      <div
        style={{
          width: "100%",
        }}
      >
        <div className="mb-1 f-pre-line">{address.companyName}</div>
        {getAddressLine1(address.lines)}
        {getAddressLine2(address.lines)}
        <div className="mb-2">{t(address.country.name)}</div>
      </div>
      {address.editable && (
        <Button
          variant="text"
          data-test-id={"checkout-dropdown-address-btn-edit-item"}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
            setIsModalOpen(true);
            setAddressChosenToEdit(address);

            setSelectedIndex((prev) => prev);
          }}
          startIcon={<Icon type={IconType.Pencil} />}
        >
          {t("page.linear.summary.btn.edit")}
        </Button>
      )}
      {address.default && (
        <Typography
          variant="labelSmall"
          sx={{
            minWidth: "148px",
          }}
        >
          {t("checkoutPage.address.dialog.defaultAddress")}
        </Typography>
      )}
    </Stack>
  );

  if (!isOpenAddress) {
    return null;
  }

  const renderAddressList = (addresses: AddressData[]) => {
    let lastCategory = "";

    function getCategory(address: AddressData) {
      if (address.default) {
        return "default";
      }
      if (address.editable) {
        return "editable";
      }
      return "approved";
    }

    return (
      <>
        {addresses.map((address: AddressData, index: number) => {
          const category = getCategory(address);
          const showTitle = category !== lastCategory;
          lastCategory = category;

          return (
            <div key={address.id}>
              {showTitle && !address.default && (
                <Typography variant="BodyMedium" paddingBottom={1}>
                  {renderAddressesLabel(address.default, address.editable)}
                </Typography>
              )}
              {renderAddressCard(address, index)}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Modal title={t("checkoutPage.address.dialog.selectAddress")} onClose={onClose} footerChildren={footer()}>
        <Stack gap={1}>{renderAddressList(sortedAddresses)}</Stack>
      </Modal>
      {isModalOpen && (
        <CreateEditAddressModal
          onClose={() => {
            setIsModalOpen(false);
          }}
          address={addressChosenToEdit}
          countriesList={countriesList}
          onDelete={(address) => {
            // wtf dis?
            if (idOfSelectedAddress === address.id) {
              updateAddress(defaulAddress, defaulAddress.temporary);
            }
            deleteAddress({}, address.id, address.temporary);
            deleteAdressOnFront(address.id);
            setIsModalOpen(false);
          }}
          onSave={async (address) => {
            const isNew = !address.id;

            const body = {
              company: address.companyName,
              houseNumber: address.houseNumber,
              street: address.street,
              addressTwo: address.addressAdditional,
              zipCode: address.zipCode,
              city: address.city,
              country: address.country,
              savable: address.persistForLater,
            };

            const country = countriesList.find((country) => country.isocode === address.country)!;

            const addressData: AddressData = {
              id: address.id ?? "",
              companyName: address.companyName,
              lines: [createAddressLine1(address.houseNumber, address.street), address.addressAdditional],
              postalCode: address.zipCode,
              town: address.city,
              country,
              temporary: !address.persistForLater,
              default: false,
              editable: true,
              state: "",
            };

            if (isNew) {
              const res = await createAddress(body);
              const addressId = await res.json();
              addressData.id = addressId;
              addAdressOnFront(addressData);
              setIsModalOpen(false);
            } else {
              editAddress(body, address.id!);
              // populateAddress(frontNewAddress);
              editAdressOnFront(address.id!, addressData);
              setIsModalOpen(false);
            }
          }}
        />
      )}
    </>
  );
};

export default memo(AddressDropdown);

const FooterStack = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
});
